import { ref, computed, reactive } from 'vue'
import { defineStore } from 'pinia'
import { DAY_TYPE, PERIOD_STATUS, type Employee, type DailyTime, type Payslip, type Period, type Schedule } from './types'
import { faker } from '@faker-js/faker'
import { useEmployeeStore } from './employee'
import { usePayslipStore } from './payslip'
import { useAppStore } from './app'
import { useTableStore } from './table'
import { usePeriodStore } from './period'
import { useScheduleStore } from './schedule'

import dayjs from 'dayjs'
import axios from 'axios'
import { useUtil } from '@/composables/util'
import {
  readSwipes,
  normalizeId,
  shiftWindow,
  shiftDateKey,
  normalizeLogs,
  mergeLogs,
  isIncomplete,
  readiness,
  periodDays,
  generatedLogs,
  joinLogs,
  type Issue,
  type SkippedRow,
} from '@/composables/dailytimeLogs'

const LOG_FORMAT_STR = 'YYYY-MM-DD HH:mm:ss'
const my = useUtil()


const apiURL: string = import.meta.env.VITE_API_URL + '/api'
// const apiURL = "http://localhost:4000/api"

const api = axios.create({
  baseURL: apiURL + '/v1'
})

export interface DailyTimeImportSummary {
  ok: boolean
  /** why nothing was imported (no period, period closed, empty file) */
  fatal: Issue[]
  rows: number
  swipes: number
  created: number
  updated: number
  unchanged: number
  /** swipes that belong to a shift outside the period */
  outsidePeriod: number
  /** rows that could not be filed, grouped by reason, with their row numbers */
  skipped: Array<{ reason: string; rows: number[] }>
  /** days imported with a missing clock-in or clock-out */
  incomplete: Array<{ id: string; name: string; date: string }>
  failed: Array<{ id: string; name: string; date: string; error: unknown }>
  warnings: string[]
  dateOrder: 'MDY' | 'DMY'
}

export interface DailyTimeGenerateResult {
  ok: boolean
  issues: Issue[]
  created: number
  deleted: number
  kept: number
  failed: number
}

export const useDailyTimeStore = defineStore('dailytime', () => {
  const DayTypes = Object.values({ ...DAY_TYPE })
  const dailyTimes = reactive<DailyTime[]>([])
  const selectedDailyTime = ref({} as DailyTime) // selected in table
  const thisDailyTime = ref({} as DailyTime) //for form editing

  const timelogs = ref({} as any)
  const thisLog = ref<any>(dayjs())

  const showForm = ref(false)

  const periodStore = usePeriodStore()
  const employeeStore = useEmployeeStore()
  const payslipStore = usePayslipStore()
  const scheduleStore = useScheduleStore()

  const appStore = useAppStore()
  const tableStore = useTableStore()

  async function generateDailyTimes(count = 200) {
    if (dailyTimes.length > 0) {
      return
    }

    const year = dayjs().get('year')
    const month = dayjs().get('month')

    const start_date = dayjs().startOf('month')
    const end_date = dayjs(start_date).add(15, 'day')

    const hour_in = 8
    const hour_out = 17

    payslipStore.payslips.forEach((payslip: Payslip) => {
      let day_date = start_date
      while (day_date < end_date) {
        const day = dayjs(day_date).get('date')

        const dailytime = {} as DailyTime

        dailytime.day_type = DAY_TYPE.WorkingDay
        dailytime.payslip_uuid = payslip.uuid

        const emp = employeeStore.employees.find(
          (employee: Employee) => employee.uuid == payslip.employee_uuid
        )
        dailytime.employee_uuid = emp?.uuid
        dailytime.employee_id = emp?.employee_id

        dailytime.time_in = dayjs()
          .set('year', year)
          .set('month', month)
          .set('date', day)
          .set('hour', hour_in)
          .set('minute', 0)
          .set('second', 0)
          .toDate()

        dailytime.time_out = dayjs()
          .set('year', year)
          .set('month', month)
          .set('date', day)
          .set('hour', hour_out)
          .set('minute', 0)
          .set('second', 0)
          .toDate()
        addDailyTime(dailytime)
        day_date = dayjs(day_date).add(1, 'day')
      }
    })
  }

  // recalc immediately when the payslip belongs to the selected Open period
  // (App.vue keeps that period's daily times and transactions loaded)
  function recalcPayslipNow(payslip_uuid?: string) {
    const payslip = payslipStore.payslips.find((item: Payslip) => item?.uuid == payslip_uuid)
    if (payslip != undefined && appStore.thisPeriod?.status == PERIOD_STATUS.Open) {
      payslipStore.calcPay(payslip)
    }
  }

  /**
   * Creates a daily time. Returns the stored row, or null when the server refused it (its reasons
   * are left in appStore.errors). `dirty: false` lets a bulk caller mark the payslip once at the end.
   */
  async function addDailyTime(payload: DailyTime, dirty: boolean = true) {
    const response = await api.post('/dailytimes.json', payload)

    if (response.status == 200) {
      const errors = response.data?.errors
      if (errors && Object.keys(errors).length > 0) {
        appStore.errors = errors
        return null
      }
      dailyTimes.push(response.data)
      if (dirty) payslipStore.dirtyPayslip(payload.payslip_uuid as string)
      return response.data as DailyTime
    }
    return null
  }

  async function saveDailyTime(payload: DailyTime) {
    if (payload.uuid) {
      const response = await api.put('/dailytimes/' + payload.uuid, payload)

      if (response.status == 200) {
        const idx = dailyTimes.findIndex((item: any) => item.uuid == payload.uuid)
        // the v1 PUT returns the row flat — response.data.data is undefined
        // and used to replace the edited row with undefined, crashing the
        // next recalc's day loop
        dailyTimes.splice(idx, 1, response.data?.data ?? response.data)
      } else {
      }
    } else {
      await addDailyTime(payload)
    }
    await payslipStore.dirtyPayslip(payload.payslip_uuid as string)
    recalcPayslipNow(payload.payslip_uuid)
  }

  // Repoint a payslip's daily-time rows to a schedule (e.g. the employee's
  // ShiftCode changed), keeping logs intact. Persists each changed row; the
  // caller recalcs once afterwards. Returns the count of rows changed.
  async function setPayslipSchedule(payslip_uuid: string, schedule_uuid?: string) {
    const rows = dailyTimes.filter(
      (dt) => dt?.payslip_uuid == payslip_uuid && dt.schedule_uuid != schedule_uuid
    )
    for (const dt of rows) {
      const response = await api.put('/dailytimes/' + dt.uuid, { ...dt, schedule_uuid })
      if (response.status == 200) {
        const idx = dailyTimes.findIndex((item) => item?.uuid == dt.uuid)
        dailyTimes.splice(idx, 1, response.data?.data ?? response.data)
      }
    }
    return rows.length
  }

  const selectedPayslipDailyTimes = computed(() => {
    const ds = dailyTimes.filter(item => item != undefined).filter((item: DailyTime) => item.payslip_uuid == payslipStore.thisPayslip.uuid)
    return ds
  })

  // recalc=false lets bulk callers (generatePayslipDailyTimes) recalc once at the end
  async function deleteDailyTime(payload: DailyTime, recalc: boolean = true) {
    const response = await api.delete('/dailytimes/' + payload.uuid)
    // record was actually deleted
    if (response.status == 200) {
      const ndx = dailyTimes.findIndex(
        (item: DailyTime) => item.uuid == payload.uuid
      );
      dailyTimes.splice(ndx, 1)
      if (recalc) {
        await payslipStore.dirtyPayslip(payload.payslip_uuid as string)
        recalcPayslipNow(payload.payslip_uuid)
      }
    }
  }

  const selectedPeriodDailyTimes = computed(() => {
    const ds = dailyTimes.filter((item: DailyTime) => item.period_uuid == appStore.thisPeriod.uuid)
    return ds
  })

  const hoursTally = computed(() => (dailytime: DailyTime) => {
    const time_in = dayjs(dailytime.time_in)
    const time_out = dayjs(dailytime.time_out)
    const hours = time_out.diff(time_in, 'hour')
    return hours
  })

  const hours = computed(() => (dailytime: DailyTime) => {
    const time_in = dayjs(dailytime.time_in)
    const time_break = dayjs(dailytime.time_break)
    const time_resume = dayjs(dailytime.time_resume)
    const time_out = dayjs(dailytime.time_out)

    const hours =
      (time_out.diff(time_in, 'minute') - (time_resume.diff(time_break, 'minute') || 60)) / 60
    return hours
  })

  const undertime = computed(() => (dailytime: DailyTime) => {
    const time_in = dayjs(dailytime.time_in)
    const time_break = dayjs(dailytime.time_break)
    const time_resume = dayjs(dailytime.time_resume)
    const time_out = dayjs(dailytime.time_out)
    const hours =
      (time_out.diff(time_in, 'minute') - (time_resume.diff(time_break, 'minute') || 60)) / 60

    const undertime = hours < 8 ? 8 - hours : 0

    return undertime
  })

  const overtime = computed(() => (dailytime: DailyTime) => {
    const time_in = dayjs(dailytime.time_in)
    const time_break = dayjs(dailytime.time_break)
    const time_resume = dayjs(dailytime.time_resume)
    const time_out = dayjs(dailytime.time_out)
    const hours =
      (time_out.diff(time_in, 'minute') - (time_resume.diff(time_break, 'minute') || 60)) / 60

    const overtime = hours > 8 ? hours - 8 : 0

    return overtime
  })

  /** Runs `work` over `items`, a few at a time; the results come back in order. */
  async function runPool<T, R>(items: T[], size: number, work: (item: T) => Promise<R>): Promise<R[]> {
    const results: R[] = new Array(items.length)
    let next = 0
    const lane = async () => {
      while (next < items.length) {
        const mine = next++
        results[mine] = await work(items[mine])
      }
    }
    await Promise.all(Array.from({ length: Math.min(size, items.length) }, lane))
    return results
  }

  const COMPANY_ON = () => !!appStore.options?.companyEnabled
  const dateDigits = (value?: string) => String(value ?? '').replace(/\D/g, '')
  const INCOMPLETE_MARK = 'INCOMPLETE LOGS'
  const fullName = (e?: Employee) => (e ? `${e.lastname ?? ''}, ${e.firstname ?? ''}`.trim() : '')

  // these stop one employee's rows (the shift cannot be read, nothing to attach them to);
  // the rest are worth telling the user about but do not stop the import
  const BLOCKING = ['no-employee', 'no-employee-id', 'no-payslip', 'no-schedule', 'schedule-missing', 'bad-shift', 'no-company']

  const importWarnings = ref<string[]>([])

  /**
   * Imports a biometric/time-clock file into the selected period's daily times.
   *
   * Checks first that the period is open and complete, then for each employee in the file that
   * the employee, their payslip in this period, their shift and (if the Company feature is on)
   * their company are all in place; rows that cannot be filed are reported, never dropped quietly.
   * Swipes are read in any common date format, filed under the day of the shift they belong to
   * (night shifts included), merged into any daily time the day already has (so importing a file
   * twice changes nothing) and written a few at a time, all awaited. A day with a missing clock-in
   * or clock-out is imported and flagged.
   */
  const importDailyTime = async (rows: any[]): Promise<DailyTimeImportSummary> => {
    const period = appStore.thisPeriod
    const summary: DailyTimeImportSummary = {
      ok: true,
      fatal: [],
      rows: rows?.length ?? 0,
      swipes: 0,
      created: 0,
      updated: 0,
      unchanged: 0,
      outsidePeriod: 0,
      skipped: [],
      incomplete: [],
      failed: [],
      warnings: [],
      dateOrder: 'MDY',
    }

    const fatal = readiness({ period: period as any, requireOpen: true })
    if (fatal.length) {
      summary.ok = false
      summary.fatal = fatal
      return summary
    }
    if (!Array.isArray(rows) || rows.length === 0) {
      summary.ok = false
      summary.fatal = [{ code: 'empty-file', message: 'The file has no rows.' }]
      return summary
    }

    const read = readSwipes(rows)
    summary.dateOrder = read.order
    summary.swipes = read.swipes.length
    const skip = new Map<string, number[]>()
    const addSkip = (reason: string, row: number) => skip.set(reason, [...(skip.get(reason) ?? []), row])
    read.skipped.forEach((s: SkippedRow) => addSkip(s.reason, s.row))

    // who is who, once
    const byNumber = new Map<string, Employee[]>()
    employeeStore.employees.forEach((e: Employee) => {
      const key = normalizeId(e.employee_id)
      byNumber.set(key, [...(byNumber.get(key) ?? []), e])
    })
    const payslipOf = new Map<string, Payslip>()
    payslipStore.payslips
      .filter((p: Payslip) => p?.period_uuid == period.uuid)
      .forEach((p: Payslip) => payslipOf.set(p.employee_uuid as string, p))
    const days = new Set(periodDays(period))

    interface Who { employee: Employee; payslip: Payslip; schedule: Schedule; window: ReturnType<typeof shiftWindow>; company?: string }
    const who = new Map<string, Who | string>() // a Who, or the reason there is none
    const resolve = (id: string, rawId: string): Who | string => {
      if (who.has(id)) return who.get(id) as Who | string
      let outcome: Who | string
      const matches = byNumber.get(id) ?? []
      if (matches.length === 0) outcome = `no employee with number ${rawId}`
      else if (matches.length > 1) outcome = `employee number ${rawId} belongs to ${matches.length} employees (${matches.map(fullName).join('; ')})`
      else {
        const employee = matches[0]
        const payslip = payslipOf.get(employee.uuid)
        const schedule = scheduleStore.schedules.find((s: Schedule) => s.uuid == (payslip?.schedule_uuid ?? employee.schedule_uuid))
        const issues = readiness({
          period: period as any, employee: employee as any, payslip: (payslip ?? null) as any, schedule: schedule as any,
          companyEnabled: COMPANY_ON(), company_uuid: appStore.thisCompany?.uuid,
        })
        const blocking = issues.filter((i: Issue) => BLOCKING.includes(i.code))
        issues.filter((i: Issue) => !BLOCKING.includes(i.code)).forEach((i: Issue) => {
          const text = `${fullName(employee)}: ${i.message}`
          if (!summary.warnings.includes(text)) summary.warnings.push(text)
        })
        outcome = blocking.length
          ? `${fullName(employee)} (${rawId}): ${blocking.map((i: Issue) => i.message.replace(/\.$/, '')).join('; ')}`
          : {
              employee, payslip: payslip as Payslip, schedule: schedule as Schedule, window: shiftWindow(schedule),
              company: (payslip as any)?.company_uuid || (employee as any).company_uuid || appStore.thisCompany?.uuid,
            }
      }
      who.set(id, outcome)
      return outcome
    }

    // file each swipe under its shift's day
    const days_: Map<string, { who: Who; date: string; swipes: string[] }> = new Map()
    for (const swipe of read.swipes) {
      const found = resolve(swipe.id, swipe.rawId)
      if (typeof found === 'string') {
        addSkip(found, swipe.row)
        continue
      }
      const date = shiftDateKey(swipe.at, found.window)
      if (!days.has(date)) {
        summary.outsidePeriod++
        continue
      }
      const key = `${found.payslip.uuid}|${date}`
      const day = days_.get(key) ?? { who: found, date, swipes: [] }
      day.swipes.push(swipe.at)
      days_.set(key, day)
    }

    summary.skipped = [...skip.entries()].map(([reason, rowsSkipped]) => ({ reason, rows: rowsSkipped }))

    // what the days already hold
    const existing = new Map<string, DailyTime>()
    dailyTimes.filter((d) => d).forEach((d: DailyTime) => existing.set(`${d.payslip_uuid}|${dateDigits(d.date_)}`, d))

    timelogs.value = {}
    const touched = new Set<string>()
    const work: Array<() => Promise<void>> = []

    for (const [key, day] of days_) {
      const swipes = normalizeLogs(day.swipes).swipes
      const old = existing.get(key)
      const incomplete = isIncomplete(old ? normalizeLogs(mergeLogs(old.logs, swipes)).swipes : swipes)
      const label = { id: day.who.employee.employee_id, name: fullName(day.who.employee), date: day.date }
      timelogs.value[day.who.employee.employee_id] = { ...(timelogs.value[day.who.employee.employee_id] ?? {}), [day.date]: { logs: joinLogs(swipes) } }
      if (incomplete) summary.incomplete.push(label)

      const mark = (current?: string) => {
        const plain = String(current ?? '')
        if (incomplete) return plain || INCOMPLETE_MARK
        return plain.startsWith(INCOMPLETE_MARK) ? '' : plain
      }

      if (old) {
        const merged = mergeLogs(old.logs, swipes)
        const remarks = mark(old.remarks)
        if (merged === joinLogs(normalizeLogs(old.logs).swipes) && remarks === String(old.remarks ?? '')) {
          summary.unchanged++
          continue
        }
        work.push(async () => {
          try {
            await saveRow({ ...old, logs: merged, remarks })
            summary.updated++
            touched.add(day.who.payslip.uuid as string)
          } catch (e: any) {
            summary.failed.push({ ...label, error: e?.response?.data || e?.message || 'save failed' })
          }
        })
      } else {
        const md = dayjs(day.date, 'YYYYMMDD').format('M/D')
        const holiday = tableStore.HOLIDAY.find((h: any) => h.Day == md)
        const payload = {
          period_uuid: period.uuid,
          payslip_uuid: day.who.payslip.uuid,
          employee_uuid: day.who.employee.uuid,
          employee_id: day.who.employee.employee_id,
          schedule_uuid: day.who.schedule.uuid,
          company_uuid: day.who.company,
          cost_uuid: (day.who.payslip as any).cost_uuid,
          date_: day.date,
          logs: joinLogs(swipes),
          day_type: holiday ? holiday.Type : DAY_TYPE.WorkingDay,
          remarks: mark(''),
          early_in: false, late_out: false, late_break: false, early_resume: false,
        } as unknown as DailyTime
        work.push(async () => {
          try {
            const row = await addDailyTime(payload, false)
            if (row) {
              summary.created++
              touched.add(day.who.payslip.uuid as string)
            } else {
              summary.failed.push({ ...label, error: JSON.stringify(appStore.errors ?? 'refused') })
            }
          } catch (e: any) {
            summary.failed.push({ ...label, error: e?.response?.data || e?.message || 'save failed' })
          }
        })
      }
    }

    await runPool(work, 4, (job) => job())

    // one recalculation mark per payslip, not one per day
    for (const uuid of touched) await payslipStore.dirtyPayslip(uuid)
    return summary
  }

  /** Writes an existing row back; the stored copy in the list is replaced by what the server returns. */
  async function saveRow(row: DailyTime) {
    const response = await api.put('/dailytimes/' + row.uuid, row)
    const idx = dailyTimes.findIndex((item) => item?.uuid == row.uuid)
    const stored = response.data?.data ?? response.data
    if (idx >= 0) dailyTimes.splice(idx, 1, stored)
    return stored
  }

  async function getPeriodDailyTimes(payload: Period) {
    const response = await api.get('/dailytimes/period_uuid/' + payload.uuid)

    if (response.status == 200) {
      dailyTimes.length = 0
      response.data.data.forEach((item: DailyTime) => {
        dailyTimes.push(item)
      })
    } else {
    }
  }

  /**
   * Creates a default day for each of the shift's work days in the selected period: clock-in and
   * clock-out taken from the payslip's shift, minutes included, the clock-out on the next day for
   * a night shift. Replaces the working days (WD) the payslip already has; leave, holiday, absence
   * and other days someone entered are kept, and no working day is generated over them.
   * Returns what it did, or the reasons it could not (nothing is changed then).
   */
  async function generatePayslipDailyTimes(payload: Payslip): Promise<DailyTimeGenerateResult> {
    const period = appStore.thisPeriod
    const employee = employeeStore.employees.find((e: Employee) => e.uuid == payload.employee_uuid)
    const schedule = scheduleStore.schedules.find((item: Schedule) => item.uuid == payload.schedule_uuid)
    const issues = readiness({
      period: period as any, employee: (employee ?? null) as any, payslip: payload as any, schedule: schedule as any,
      companyEnabled: COMPANY_ON(), company_uuid: appStore.thisCompany?.uuid, requireOpen: true,
    }).filter((i: Issue) => BLOCKING.includes(i.code) || ['no-period', 'bad-period', 'period-closed'].includes(i.code))
    const result: DailyTimeGenerateResult = { ok: issues.length === 0, issues, created: 0, deleted: 0, kept: 0, failed: 0 }
    if (!result.ok) return result

    // work days of the shift, e.g. "M,T,W,Th,F"; empty means every day
    const DAY_CODES = ['Su', 'M', 'T', 'W', 'Th', 'F', 'S']
    const work_days = String(schedule?.work_days ?? '').split(',').map((d: string) => d.trim()).filter((d: string) => d != '')

    // STEP #1: remove the payslip's working days, one after the other, and keep everything else
    const mine = dailyTimes.filter((item: DailyTime) => item && item.payslip_uuid == payload.uuid)
    const keep = mine.filter((item) => item.day_type && item.day_type !== DAY_TYPE.WorkingDay)
    const replace = mine.filter((item) => !keep.includes(item))
    const kept = new Set(keep.map((item) => dateDigits(item.date_)))
    await runPool(replace, 4, async (item) => {
      try {
        await deleteDailyTime(item, false)
        result.deleted++
      } catch {
        result.failed++
      }
    })
    result.kept = keep.length

    // STEP #2: create the new days
    const wanted = periodDays(period).filter((date) => {
      if (kept.has(date)) return false
      if (work_days.length === 0) return true
      return work_days.includes(DAY_CODES[dayjs(date, 'YYYYMMDD').day()])
    })
    await runPool(wanted, 4, async (date) => {
      const dailytime = {
        day_type: DAY_TYPE.WorkingDay,
        date_: date,
        payslip_uuid: payload.uuid,
        period_uuid: payload.period_uuid,
        employee_uuid: payload.employee_uuid,
        employee_id: payload.employee_id,
        schedule_uuid: payload.schedule_uuid,
        cost_uuid: payload.cost_uuid,
        company_uuid: (payload as any).company_uuid || (employee as any)?.company_uuid || appStore.thisCompany?.uuid,
        logs: generatedLogs(date, schedule as any),
      } as unknown as DailyTime
      try {
        const row = await addDailyTime(dailytime, false)
        if (row) result.created++
        else result.failed++
      } catch {
        result.failed++
      }
    })
    await payslipStore.dirtyPayslip(payload.uuid as string)
    return result
  }

  const hasMissingFields = computed(() => (dailytime: any) => {
    if (!dailytime['schedule_uuid']
      || !dailytime['employee_uuid']
      || !dailytime['employee_id']
      || !dailytime['period_uuid']
      || (!dailytime['company_uuid'] && appStore.options.companyEnabled)
      || (!dailytime['cost_uuid'] && appStore.options.costEnabled)
      || !dailytime['payslip_uuid']
    ) {
      return true
    } else {
      return false
    }
  })

  return {
    showForm,
    dailyTimes,
    selectedDailyTime,
    thisDailyTime,
    selectedPayslipDailyTimes,
    hoursTally,
    hours,
    undertime,
    overtime,
    DayTypes,
    timelogs,
    thisLog,
    selectedPeriodDailyTimes,
    generateDailyTimes,
    generatePayslipDailyTimes,
    setPayslipSchedule,
    saveDailyTime,
    addDailyTime,
    deleteDailyTime,
    importDailyTime,
    getPeriodDailyTimes,

    hasMissingFields
  }
})
